<template>
    <div class="onboarding-creator-reinstatement text-left">
        <div v-if="loading" class="text-center py-4">
            <b-spinner variant="secondary" />
        </div>

        <div v-else>
            <b-alert v-if="error" variant="warning" show class="small">
                {{
                    $t(
                        "We couldn't load your reinstatement status. Please try again in a moment."
                    )
                }}
            </b-alert>

            <b-alert variant="warning" show class="d-flex align-items-center">
                <font-awesome-icon
                    icon="fa-duotone fa-triangle-exclamation"
                    size="lg"
                    class="mr-2"
                />
                <span>{{ $t("Your account is restricted.") }}</span>
            </b-alert>
            <p>
                {{
                    $t(
                        "You can lift the restriction by inviting a new creator from one of the countries below. Once she completes her first withdrawal, your account is reviewed for reinstatement."
                    )
                }}
            </p>

            <b-alert
                v-if="progressState === 'underReview'"
                variant="success"
                show
                class="mt-3"
            >
                <h5 class="mb-2">
                    {{ $t("Your account is under review") }}
                </h5>
                <div class="small">
                    {{
                        $t(
                            "We received your qualifying invite. Most reviews complete within 24 hours, and you will receive an email when your account is reinstated."
                        )
                    }}
                </div>
            </b-alert>

            <b-alert
                v-if="progressState === 'scheduled'"
                variant="success"
                show
                class="mt-3"
            >
                <h5 class="mb-2">
                    {{ $t("Your account is approved for reinstatement") }}
                </h5>

                <b-table-simple bordered small class="mb-2">
                    <tbody>
                        <tr>
                            <td class="w-50">
                                {{ $t("Time until restore") }}
                            </td>
                            <td class="bg-white">
                                <live-timestamp-future
                                    :timestamp="
                                        creatorReinstatement.reinstateAt
                                    "
                                />
                            </td>
                        </tr>
                    </tbody>
                </b-table-simple>

                <div class="small">
                    {{
                        $t(
                            "You will receive an email when your access is restored."
                        )
                    }}
                </div>
                <div class="small mt-2">
                    {{
                        $t(
                            "You can keep inviting creators from any country in the meantime."
                        )
                    }}
                </div>
            </b-alert>

            <div v-if="showRecruitmentSections" class="mt-4">
                <h5 class="mb-2">
                    {{
                        $t(
                            "We are looking for new creators from these countries:"
                        )
                    }}
                </h5>

                <b-row no-gutters>
                    <b-col
                        v-for="country in recommendedCountries"
                        :key="country.id"
                        cols="6"
                        class="p-1"
                    >
                        <b-btn
                            variant="light"
                            class="border d-flex align-items-center justify-content-center"
                            block
                            pill
                            @click="selectCountry(country)"
                        >
                            <img
                                :src="country.flagUrl"
                                :alt="country.name"
                                class="country-flag mr-2"
                            />
                            <span class="text-truncate">{{ country.name }}</span>
                        </b-btn>
                    </b-col>
                </b-row>

                <p class="small text-secondary mt-2 mb-0">
                    {{
                        $t(
                            "These are our top countries. Tap below to see all countries that qualify."
                        )
                    }}
                </p>

                <b-btn
                    variant="light"
                    class="border mt-3"
                    block
                    pill
                    @click="eligibleModalOpen = true"
                >
                    {{ $t("View all eligible countries") }}
                </b-btn>

                <b-btn
                    variant="light"
                    class="border mt-2"
                    block
                    pill
                    @click="guideModalOpen = true"
                >
                    {{ $t("How to find creators in another country") }}
                </b-btn>

                <div v-if="inviteUrl" class="mt-4 rounded p-3 bg-white">
                    <h5 class="mb-0">
                        <font-awesome-icon
                            icon="fa-duotone fa-link"
                            class="branded"
                            size="lg"
                        />
                        {{ $t("Your creator invite link") }}
                    </h5>

                    <div class="mt-2 small text-secondary">
                        {{
                            $t(
                                "Share this link with creators you recruit. It tracks them back to you for reinstatement."
                            )
                        }}
                        <font-awesome-icon icon="fa-light fa-arrow-down" />
                    </div>

                    <div class="mt-2">
                        <b-input-group>
                            <b-form-input :value="inviteUrl" readonly />
                            <b-input-group-append>
                                <b-btn variant="primary" @click="copyInviteUrl">
                                    {{ $t("Copy") }}
                                </b-btn>
                            </b-input-group-append>
                        </b-input-group>

                        <b-alert
                            v-if="inviteUrlCopied"
                            variant="success"
                            show
                            dismissible
                            class="small mt-2 mb-0"
                            @dismissed="inviteUrlCopied = false"
                        >
                            <h5>
                                <font-awesome-icon icon="fa-light fa-check" />
                                {{ $t("Copied") }}
                            </h5>
                            {{
                                $t(
                                    "Link copied. Send it to creators through WhatsApp, Telegram, or any messaging app. They need to open your link before signing up for you to get credit."
                                )
                            }}
                        </b-alert>
                    </div>
                </div>
            </div>

            <div v-if="balance !== null" class="mt-4 rounded p-3 bg-white">
                <h5 class="mb-3">
                    <font-awesome-icon
                        icon="fa-duotone fa-wallet"
                        class="branded"
                        size="lg"
                    />
                    {{ $t("Your balance") }}
                </h5>

                <b-row class="text-center">
                    <b-col cols="6">
                        <h1 class="text-success mb-0">{{ balance }}</h1>
                        <div class="lead">
                            <font-awesome-icon
                                class="text-warning"
                                icon="fa-duotone fa-coins"
                            />
                            {{ $t("Credits") }}
                        </div>
                    </b-col>
                    <b-col cols="6">
                        <h1 class="text-success mb-0">{{ balanceLocal }}</h1>
                        <div class="lead">{{ currency }}</div>
                    </b-col>
                </b-row>

                <p class="small text-secondary mt-3 mb-0">
                    {{
                        $t(
                            "Once your account is reinstated, your balance becomes eligible for withdrawal. You will continue to earn commissions from creators you invite."
                        )
                    }}
                </p>
            </div>

            <b-alert
                v-if="progressState === 'default'"
                variant="warning"
                show
                class="mt-4"
            >
                <h5 class="mb-2">{{ $t("Rules") }}</h5>
                <ul class="mb-0 pl-3">
                    <li>
                        {{
                            $t(
                                "Do not create additional accounts. It will delay or block your reinstatement."
                            )
                        }}
                    </li>
                    <li>
                        {{
                            $t(
                                "The invited creator must be a genuine new user from a different country than your own."
                            )
                        }}
                    </li>
                    <li>
                        {{
                            $t(
                                "This is a one-time process. Once reinstated, your account will stay active as long as you follow the platform rules."
                            )
                        }}
                    </li>
                </ul>
            </b-alert>
        </div>

        <b-modal
            v-model="eligibleModalOpen"
            :title="$t('All eligible countries')"
            ok-only
            :ok-title="$t('Close')"
            scrollable
        >
            <p class="small">
                {{
                    $t(
                        "Creators from any country below qualify. The recommended list is where we are actively recruiting, but any of these countries counts."
                    )
                }}
            </p>
            <div
                v-for="group in eligibleByContinent"
                :key="group.name"
                class="mb-3"
            >
                <h6 class="text-uppercase text-secondary small mb-2">
                    {{ group.name }}
                </h6>
                <b-row no-gutters>
                    <b-col
                        v-for="country in group.countries"
                        :key="country.id"
                        cols="6"
                        class="p-1"
                    >
                        <b-btn
                            variant="light"
                            class="border d-flex align-items-center justify-content-center"
                            block
                            pill
                            @click="selectCountry(country)"
                        >
                            <img
                                :src="country.flagUrl"
                                :alt="country.name"
                                class="country-flag mr-2"
                            />
                            <span class="text-truncate">
                                {{ country.name }}
                            </span>
                        </b-btn>
                    </b-col>
                </b-row>
            </div>
        </b-modal>

        <b-modal
            v-model="guideModalOpen"
            :title="$t('How to find creators in another country')"
            ok-only
            :ok-title="$t('Close')"
            scrollable
        >
            <ol class="pl-3">
                <li class="mb-2">
                    {{ $t("Open") }}
                    <a
                        href="https://chatgpt.com"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        chatgpt.com
                    </a>
                    <span class="small text-secondary">
                        ({{ $t("opens in new tab") }})
                    </span>
                    —
                    {{ $t("it is free, no payment needed.") }}
                </li>
                <li class="mb-2">
                    {{ $t("Tell ChatGPT:") }}
                    <em>
                        "{{
                            $t(
                                "I want to find women in [country] who would be interested in earning money on a creator platform. What are the best places to look online?"
                            )
                        }}"
                    </em>
                </li>
                <li class="mb-2">
                    {{
                        $t(
                            "Read its suggestions. It will likely mention specific apps, forums, social media groups, and online communities popular in that country."
                        )
                    }}
                </li>
                <li class="mb-2">
                    {{
                        $t(
                            "Translate your message into the local language. ChatGPT can translate for you. A short, friendly message in their own language gets far more replies than English."
                        )
                    }}
                </li>
                <li class="mb-2">
                    {{
                        $t(
                            "Be honest about what the platform is. Tell them they can earn real money. Send them your invite link."
                        )
                    }}
                </li>
                <li class="mb-2">
                    {{
                        $t(
                            "Be patient. Most messages will be ignored. You only need one creator to complete her first withdrawal to get your reinstatement."
                        )
                    }}
                </li>
            </ol>
            <p class="small text-secondary mt-3 mb-0">
                {{
                    $t(
                        "After she signs up, stay close. New creators often have questions about how the app works, how to message customers, and how to withdraw their earnings. Help her find the answers and guide her toward her first withdrawal. Once she completes it, your account will be reinstated."
                    )
                }}
            </p>
        </b-modal>

        <b-modal
            :visible="selectedCountry !== null"
            @hidden="selectedCountry = null"
            ok-only
            :ok-title="$t('Close')"
            scrollable
        >
            <template #modal-title>
                <span v-if="selectedCountry" class="d-flex align-items-center">
                    <img
                        :src="selectedCountry.flagUrl"
                        :alt="selectedCountry.name"
                        class="country-flag mr-2"
                    />
                    <span>{{ selectedCountry.name }}</span>
                </span>
            </template>

            <p class="small">
                {{
                    $t(
                        "Copy any of these prompts and paste them into ChatGPT to get started. Each one is already filled in for"
                    )
                }}
                <strong v-if="selectedCountry">
                    {{ selectedCountry.name }}.
                </strong>
            </p>

            <div
                v-for="(prompt, index) in selectedCountryPrompts"
                :key="index"
                class="mb-3"
            >
                <h6 class="mb-1">{{ prompt.label }}</h6>
                <div class="bg-light border rounded p-2 small">
                    {{ prompt.text }}
                </div>
                <b-btn
                    variant="primary"
                    size="sm"
                    pill
                    class="mt-2"
                    @click="copyPrompt(index, prompt.text)"
                >
                    <font-awesome-icon
                        :icon="
                            copiedPromptIndex === index
                                ? 'fa-light fa-check'
                                : 'fa-light fa-copy'
                        "
                    />
                    {{
                        copiedPromptIndex === index
                            ? $t("Copied")
                            : $t("Copy")
                    }}
                </b-btn>
            </div>

            <p class="small text-secondary mb-0">
                {{
                    $t(
                        "Open chatgpt.com in your browser, paste a prompt, and follow the suggestions. ChatGPT is free, no payment needed."
                    )
                }}
            </p>
        </b-modal>
    </div>
</template>

<script>
import copy from "clipboard-copy";

import LiveTimestampFuture from "@/components/utility/LiveTimestampFuture";

const REFETCH_THRESHOLD_MS = 60 * 1000;
const COPY_FEEDBACK_MS = 2000;

export default {
    components: {
        LiveTimestampFuture,
    },
    data() {
        return {
            loading: true,
            error: false,
            creatorReinstatement: null,
            recommendedCountries: [],
            eligibleCountries: [],
            inviteUrl: null,
            balance: null,
            balanceLocal: null,
            currency: null,
            inviteUrlCopied: false,
            eligibleModalOpen: false,
            guideModalOpen: false,
            selectedCountry: null,
            copiedPromptIndex: null,
            lastFetchedAt: null,
        };
    },
    computed: {
        progressState() {
            if (!this.creatorReinstatement) {
                return "default";
            }
            if (!this.creatorReinstatement.reinstateAt) {
                return "underReview";
            }
            return "scheduled";
        },
        showRecruitmentSections() {
            return this.progressState !== "scheduled";
        },
        eligibleByContinent() {
            const order = [
                "South America",
                "North America",
                "Europe",
                "Asia",
                "Africa",
                "Oceania",
            ];
            const groups = {};
            for (const country of this.eligibleCountries) {
                const key = country.continentName || "Other";
                if (!groups[key]) {
                    groups[key] = [];
                }
                groups[key].push(country);
            }
            return order
                .filter((name) => groups[name])
                .map((name) => ({ name, countries: groups[name] }))
                .concat(
                    Object.keys(groups)
                        .filter((name) => !order.includes(name))
                        .map((name) => ({ name, countries: groups[name] }))
                );
        },
        selectedCountryPrompts() {
            if (!this.selectedCountry) {
                return [];
            }
            const name = this.selectedCountry.name;
            return [
                {
                    label: this.$t("Find prospects"),
                    text: this.$t(
                        `I want to find women in ${name} who might be interested in earning money on a creator platform. What are the best apps, websites, forums, social media groups, or online communities popular in ${name} where I can reach them?`
                    ),
                },
                {
                    label: this.$t("Translate a friendly intro"),
                    text: this.$t(
                        `Translate this short, friendly message into the most common language spoken by young women in ${name}, in a casual and warm tone: "Hi! I want to share an opportunity to earn real money from your phone. It is safe, you work from home, and you set your own hours. Want to learn more?"`
                    ),
                },
                {
                    label: this.$t("Cultural context"),
                    text: this.$t(
                        `What language do most young women in ${name} speak online? Are there any cultural points I should keep in mind when introducing them to online work?`
                    ),
                },
            ];
        },
    },
    created() {
        this.fetchStatus();
    },
    mounted() {
        document.addEventListener(
            "visibilitychange",
            this.handleVisibilityChange
        );
    },
    beforeDestroy() {
        document.removeEventListener(
            "visibilitychange",
            this.handleVisibilityChange
        );
    },
    methods: {
        fetchStatus() {
            this.loading = this.lastFetchedAt === null;
            this.error = false;

            this.api
                .post("/creatorReinstatement/status")
                .then((response) => {
                    this.creatorReinstatement =
                        response?.data?.creatorReinstatement || null;
                    this.recommendedCountries =
                        response?.data?.recommendedCountries || [];
                    this.eligibleCountries =
                        response?.data?.eligibleCountries || [];
                    this.inviteUrl = response?.data?.inviteUrl || null;
                    this.balance = response?.data?.balance ?? null;
                    this.balanceLocal = response?.data?.balanceLocal ?? null;
                    this.currency = response?.data?.currency || null;
                    this.lastFetchedAt = Date.now();
                })
                .catch(() => {
                    this.error = true;
                })
                .then(() => {
                    this.loading = false;
                });
        },
        handleVisibilityChange() {
            if (document.visibilityState !== "visible") {
                return;
            }
            if (
                !this.lastFetchedAt ||
                Date.now() - this.lastFetchedAt > REFETCH_THRESHOLD_MS
            ) {
                this.fetchStatus();
            }
        },
        selectCountry(country) {
            this.copiedPromptIndex = null;
            this.selectedCountry = country;
        },
        copyPrompt(index, text) {
            copy(text);
            this.copiedPromptIndex = index;
            setTimeout(() => {
                if (this.copiedPromptIndex === index) {
                    this.copiedPromptIndex = null;
                }
            }, COPY_FEEDBACK_MS);
        },
        copyInviteUrl() {
            if (!this.inviteUrl) {
                return;
            }
            copy(this.inviteUrl);
            this.inviteUrlCopied = true;
        },
    },
};
</script>

<style scoped>
.country-flag {
    width: 24px;
    height: auto;
    flex-shrink: 0;
}
</style>
